import { Bar } from 'react-chartjs-2';
import Paper from '@trackman/web-shared-components/components/Paper/Paper';
import { ChartData, ChartOptions } from 'chart.js';
import { NoData } from 'pages/Admin/Analytics/components/NoData/NoData';
import { SourceBadge } from 'pages/Admin/Analytics/components/SourceBadge/SourceBadge';
import { currencyFormatter, formatMonth, indexByMonth, mergeMonthKeys, parseMonthKey } from 'pages/Admin/Analytics/utils/monthlySeries';
import { useGetMonthlyDiscountedSalesItems, useGetMonthlyRefunds } from 'queryHooks/Admin/analytics';

const buildChartOptions = (refundLineItemCounts: number[]): ChartOptions<'bar'> => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: { boxWidth: 10, font: { size: 11 } },
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const label = context.dataset.label ?? '';
          const value = Number(context.raw ?? 0);
          return context.dataset.yAxisID === 'refundAmount'
            ? `${label}: ${currencyFormatter.format(value)}`
            : `${label}: ${value.toLocaleString()}`;
        },
        afterLabel: (context) => {
          if (context.dataset.yAxisID !== 'refundAmount') return '';
          const count = refundLineItemCounts[context.dataIndex];
          return count !== undefined ? `Refund line items: ${count}` : '';
        },
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 11 } },
    },
    refundAmount: {
      type: 'linear',
      position: 'left',
      grid: { color: 'rgba(0,0,0,0.06)' },
      ticks: {
        font: { size: 11 },
        callback: (value) => currencyFormatter.format(Number(value)),
      },
    },
    discountedItems: {
      type: 'linear',
      position: 'right',
      grid: { drawOnChartArea: false },
      ticks: {
        font: { size: 11 },
        precision: 0,
      },
    },
  },
});

export const RefundsChart = () => {
  const { data: refundsData, isLoading: isLoadingRefunds, isError: isRefundsError } = useGetMonthlyRefunds();
  const {
    data: discountedItemsData,
    isLoading: isLoadingDiscountedItems,
    isError: isDiscountedItemsError,
  } = useGetMonthlyDiscountedSalesItems();

  const isLoading = isLoadingRefunds || isLoadingDiscountedItems;
  const isError = isRefundsError || isDiscountedItemsError;
  const hasData = Boolean(refundsData?.length || discountedItemsData?.length);

  const keys = mergeMonthKeys(refundsData, discountedItemsData);

  const refundsByMonth = indexByMonth(refundsData);
  const discountedItemsByMonth = indexByMonth(discountedItemsData);

  const refundLineItemCounts = keys.map((key) => refundsByMonth.get(key)?.count ?? 0);

  const chartData: ChartData<'bar'> = {
    labels: keys.map((key) => {
      const { year, month } = parseMonthKey(key);
      return formatMonth(year, month);
    }),
    datasets: [
      {
        label: 'Refunds',
        data: keys.map((key) => refundsByMonth.get(key)?.totalAmount ?? 0),
        yAxisID: 'refundAmount',
        backgroundColor: 'rgba(239, 68, 68, 0.65)',
        borderRadius: 3,
      },
      {
        label: 'Discounted items',
        data: keys.map((key) => discountedItemsByMonth.get(key)?.totalCount ?? 0),
        yAxisID: 'discountedItems',
        backgroundColor: 'rgba(148, 163, 184, 0.75)',
        borderRadius: 3,
      },
    ],
  };

  return (
    <Paper className='analytics-card analytics-chart-card'>
      <div className='analytics-card__title-group'>
        <span className='card-title'>Refunds & discounts</span>
        <SourceBadge source='RECURLY' />
      </div>
      <p className='analytics-card__subtitle'>Monthly refund amount and total discounted sale item count.</p>

      {isLoading || isError || !hasData ? (
        <NoData loading={isLoading} error={isError} noData={!hasData} />
      ) : (
        <div className='analytics-chart-card__content'>
          <Bar data={chartData} options={buildChartOptions(refundLineItemCounts)} />
        </div>
      )}
    </Paper>
  );
};

export default RefundsChart;
